/* ─────────────────────────────────────────────────────────────────────────
   Widget attribution — which orders actually came out of which widget.

   Two widgets sell on their own, outside the buy box:

     refill module    "Hi, I'm refillable." on the PDP
                      snippets/product-block-refill-module.liquid
     cart cross-sell  the Rebuy Smart Cart refill offer, widget 307473
                      templated in snippets/rebuy-extensions.liquid

   Usage means bought, not clicked, so the signal has to survive a shopper
   changing their mind. Two layers do that:

     line item property   `_widget`, stamped on the line the widget added. It
                          rides to the order with that line and leaves with it
                          if the line is removed. Underscore-prefixed, so the
                          theme, Smart Cart and checkout all hide it from the
                          shopper -- every cart template here already skips
                          properties whose name starts with an underscore.

     cart attribute       `refill_widget` / `cart_cross_sell`, recomputed from
                          the cart's own lines after every cart change and set
                          to "yes" only while a matching line is still in the
                          bag. It lands on the order under "Additional details"
                          in admin, where it can be counted, filtered in the
                          Admin API, and turned into an order tag by a Flow
                          ("Order created" -> attribute equals yes -> add tag).
                          Cart attributes are never shown to the shopper.

   The refill module stamps its own line where it adds it, in
   assets/product-refill-module.js. Rebuy owns the cross-sell's add call, so
   the request gets tagged instead: a click inside the cross-sell arms a stamp
   and the next /cart/add.js carries the property. If Rebuy ever changes how
   it adds, the attribute still finds the line through Rebuy's own hidden
   attribution properties (_widget_id / _source), which name the widget id.

   Nothing here may cost a sale. Every hook falls through to the original call
   on any error, the stamp is skipped rather than retried, and the attribute
   write only ever fires when it disagrees with the cart.
   ───────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var PROPERTY = '_widget';

  // Our own reconcile write carries this header so the observer below can tell
  // it apart from a real cart change and not reconcile its own echo.
  var OWN_REQUEST_HEADER = 'X-Widget-Attribution';

  // How long after a click inside a widget an add is still that widget's. Long
  // enough for a slow Rebuy round trip, short enough that it cannot be inherited
  // by an unrelated add later on.
  var STAMP_WINDOW = 8000;

  var SOURCES = [
    {
      value: 'refill-module',
      attribute: 'refill_widget'
      // No selectors: the module stamps its own add call directly.
    },
    {
      value: 'cart-cross-sell',
      attribute: 'cart_cross_sell',
      // Rebuy widget ids that sell from inside the cart. 307473 is the Smart
      // Cart refill offer, declared in snippets/rebuy-extensions.liquid; 84998
      // is the older cross-sell that sections/quick-cart.liquid still renders.
      // Keep these in step if a widget id changes in Rebuy.
      widgetIds: ['307473', '84998'],
      // The id can change in Rebuy; the template's own root class cannot.
      selectors: ['.refill-offer', '#rebuy-widget-307473', '#rebuy-widget-84998']
    }
  ];

  function noop() {}

  function routeRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root || '/';
  }

  function pathOf(url) {
    try {
      return new URL(url, window.location.href).pathname;
    } catch (err) {
      return String(url || '');
    }
  }

  function isCartAdd(url) {
    return /\/cart\/add(\.js)?$/.test(pathOf(url));
  }

  function isCartMutation(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?$/.test(pathOf(url));
  }

  /* ── Arming a stamp ────────────────────────────────────────────────────── */

  var pending = null;

  function arm(value) {
    pending = { value: value, expires: Date.now() + STAMP_WINDOW };
  }

  function takePending() {
    if (!pending) return null;
    var value = pending.expires >= Date.now() ? pending.value : null;
    pending = null;
    return value;
  }

  function sourceForClick(target) {
    if (!target || typeof target.closest !== 'function') return null;

    // Only a button can start an add. A click on the shade select or the
    // widget's own backdrop must not arm anything.
    var button = target.closest('button, [role="button"], .rebuy-button');
    if (!button || button.disabled) return null;

    for (var i = 0; i < SOURCES.length; i++) {
      var selectors = SOURCES[i].selectors;
      if (!selectors) continue;
      for (var j = 0; j < selectors.length; j++) {
        if (button.closest(selectors[j])) return SOURCES[i];
      }
    }
    return null;
  }

  document.addEventListener(
    'click',
    function (event) {
      try {
        var source = sourceForClick(event.target);
        if (source) arm(source.value);
      } catch (err) {
        /* attribution is never worth an exception in a click handler */
      }
    },
    true
  );

  /* ── Stamping the add request ──────────────────────────────────────────── */

  function withProperty(properties, value) {
    var out = properties || {};
    // A property that is already there was set deliberately -- by the module
    // that owns the add, or by an earlier stamp. Never overwrite it.
    if (!out[PROPERTY]) out[PROPERTY] = value;
    return out;
  }

  function stampPayload(payload, value) {
    if (!payload || typeof payload !== 'object') return payload;
    if (Object.prototype.toString.call(payload.items) === '[object Array]') {
      payload.items.forEach(function (item) {
        if (item && typeof item === 'object') item.properties = withProperty(item.properties, value);
      });
    } else if (payload.id) {
      payload.properties = withProperty(payload.properties, value);
    }
    return payload;
  }

  function stampBody(body, value) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      if (!body.has('properties[' + PROPERTY + ']')) {
        body.append('properties[' + PROPERTY + ']', value);
      }
      return body;
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      if (!body.has('properties[' + PROPERTY + ']')) {
        body.append('properties[' + PROPERTY + ']', value);
      }
      return body;
    }

    if (typeof body === 'string') {
      var trimmed = body.replace(/^\s+/, '');
      if (trimmed.charAt(0) === '{') {
        return JSON.stringify(stampPayload(JSON.parse(body), value));
      }
      if (body.indexOf('properties[' + PROPERTY + ']') !== -1) return body;
      if (/(^|&)(id=|items)/.test(body)) {
        return (
          body +
          '&properties[' +
          encodeURIComponent(PROPERTY) +
          ']=' +
          encodeURIComponent(value)
        );
      }
    }

    return body;
  }

  function isOwnRequest(init) {
    var headers = init && init.headers;
    if (!headers) return false;
    if (typeof headers.get === 'function') return Boolean(headers.get(OWN_REQUEST_HEADER));
    return Boolean(headers[OWN_REQUEST_HEADER]);
  }

  var nativeFetch = window.fetch;

  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url;
      var options = init;

      try {
        // A Request object carries its body as a stream; rebuilding it to add a
        // property is not worth the risk, so those pass through untouched and
        // fall back to the attribute reading Rebuy's own properties.
        if (url && init && init.body != null && isCartAdd(url) && !isOwnRequest(init)) {
          var value = takePending();
          if (value) {
            options = {};
            for (var key in init) {
              if (Object.prototype.hasOwnProperty.call(init, key)) options[key] = init[key];
            }
            options.body = stampBody(init.body, value);
          }
        }
      } catch (err) {
        options = init;
      }

      var result = nativeFetch.call(this || window, input, options);

      try {
        if (url && isCartMutation(url) && !isOwnRequest(init)) {
          result
            .then(function (response) {
              if (response && response.ok) scheduleReconcile();
            })
            .catch(noop);
        }
      } catch (err) {
        /* observing is optional; the caller's promise is untouched either way */
      }

      return result;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    try {
      this.__widgetAttributionUrl = url;
    } catch (err) {
      /* a frozen XHR is still a working XHR */
    }
    return nativeOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    var out = body;

    try {
      var url = this.__widgetAttributionUrl;
      if (url && body != null && isCartAdd(url)) {
        var value = takePending();
        if (value) out = stampBody(body, value);
      }
      if (url && isCartMutation(url)) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) scheduleReconcile();
        });
      }
    } catch (err) {
      out = body;
    }

    return nativeSend.call(this, out);
  };

  /* ── Keeping the cart attribute honest ─────────────────────────────────── */

  function lineMatches(item, source) {
    var properties = item && item.properties;
    if (!properties) return false;
    if (properties[PROPERTY] === source.value) return true;
    if (!source.widgetIds) return false;

    // Fallback: Rebuy stamps its own hidden attribution properties (_widget_id,
    // _source, _attribution) on anything one of its widgets added. If our stamp
    // missed the request, those still name the widget.
    return Object.keys(properties).some(function (key) {
      if (!/widget|source|attribution/i.test(key)) return false;
      var value = properties[key] == null ? '' : String(properties[key]);
      return source.widgetIds.some(function (id) {
        return value.indexOf(id) !== -1;
      });
    });
  }

  var reconcileTimer = null;

  function scheduleReconcile() {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    // Let the cart settle: an add is often followed straight away by a Smart
    // Cart refresh, and one reconcile after the last of them is enough.
    reconcileTimer = setTimeout(reconcile, 700);
  }

  function reconcile() {
    reconcileTimer = null;
    if (typeof nativeFetch !== 'function') return;

    var headers = {};
    headers[OWN_REQUEST_HEADER] = '1';

    nativeFetch
      .call(window, routeRoot() + 'cart.js', {
        method: 'GET',
        credentials: 'same-origin',
        headers: headers
      })
      .then(function (response) {
        if (!response.ok) throw new Error('cart read failed');
        return response.json();
      })
      .then(function (cart) {
        if (!cart || !cart.items) return null;

        var attributes = cart.attributes || {};
        var updates = {};
        var changed = false;

        SOURCES.forEach(function (source) {
          var bought = cart.items.some(function (item) {
            return lineMatches(item, source);
          });
          var wanted = bought ? 'yes' : '';
          var current = attributes[source.attribute] || '';
          if (current !== wanted) {
            // An empty value removes the attribute, so an order never claims a
            // widget whose line was taken back out of the bag.
            updates[source.attribute] = wanted;
            changed = true;
          }
        });

        if (!changed) return null;

        var writeHeaders = { 'Content-Type': 'application/json' };
        writeHeaders[OWN_REQUEST_HEADER] = '1';

        return nativeFetch.call(window, routeRoot() + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: writeHeaders,
          body: JSON.stringify({ attributes: updates })
        });
      })
      .catch(noop);
  }

  // Rebuy can settle a cart change without a request this file sees -- an add
  // through its own transport, a Smart Cart refresh. These events cost nothing
  // when they never fire, and reconcile only writes when it disagrees with the
  // cart, so an extra trigger is free.
  ['cart:updated', 'rebuy.add', 'rebuy.afterAdd'].forEach(function (name) {
    document.addEventListener(name, scheduleReconcile);
  });
})();
